import React, { useCallback, useEffect, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/webvideo/UserPublicDataProvider";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import { MediaConstraints, ProduceStream, ProduceUser, UserListItemIcon } from "types";
import BaseAvatar from "components/BaseAvatar";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import ModalExitConfirmation from "./components/ModalExitConfirmation";
import PanelControlButtons from "./components/PanelControlButtons";
import PlayStreamButton from "./components/PlayStreamButton";
import RightPanel, { RightPanelView } from "./components/RightPanel";
import StreamsLayout from "./components/StreamsLayout";
import TransitionControl from "./components/TransitionControl";
import usePictureInPicture from "hooks/usePictureInPicture";
import { useTranslation } from "locales";
import useController from "./controller";
import useViewController from "./viewController";
import "./style.scss";

interface Props {
  className?: string;
  produce: boolean;
  produceUser: UserPublicData | null;
  threadId: string | undefined;
  loading: boolean;
  userList: LiveChatUser[];
  onClose: () => void;
  token: string;
  showConfirmationExit: boolean;
  audioInputs?: MediaDeviceInfo[];
  videoInputs?: MediaDeviceInfo[];
  managedConstraints: MediaConstraints | null;
  onChangeConstraints?: (constraints: MediaConstraints) => void;
  disableAttachments?: boolean;
  me: ProduceUser | null;
  streams: ProduceStream[];
  slotContentConfirmationExit?: JSX.Element;
  disableUserList?: boolean;
  slotTimer?: JSX.Element;
  slotVideoScreen?: JSX.Element;
  slotHeaderButtons?: JSX.Element;
  slotControlButtons?: JSX.Element;
  forceHideControlButtons?: boolean;
  userListItemIcon?: UserListItemIcon;
  enableUserSearch?: boolean;
  chatInLive?: boolean;
  includeGuestsList?: boolean;
  allowGuestMessage?: boolean;
  hideVolumeControl?: boolean;
  hideChatOnStart?: boolean;
  onSelectedStreamChange?: (streamId: string | null) => void;
}

function StreamView({
  produceUser,
  produce,
  loading,
  threadId,
  userList,
  onClose,
  token,
  managedConstraints,
  audioInputs,
  videoInputs,
  onChangeConstraints,
  disableAttachments,
  disableUserList,
  showConfirmationExit,
  forceHideControlButtons,
  slotTimer,
  slotVideoScreen,
  slotHeaderButtons,
  slotContentConfirmationExit,
  slotControlButtons,
  userListItemIcon,
  enableUserSearch,
  chatInLive,
  allowGuestMessage,
  className,
  includeGuestsList,
  me,
  streams,
  hideVolumeControl,
  hideChatOnStart,
  onSelectedStreamChange,
}: Props) {
  const { t } = useTranslation();
  const {
    changeControls,
    changeRightPanel,
    onClickEndCall,
    desktopUp,
    mobileLandscape,
    showControls,
    showControlsButtons,
    showStreamPlayButton,
    onShowControls,
    onCloseEvent,
    displayControls,
    usersCount,
    onOpenChat,
    setHasUnreadMessage,
    toggleFullscreen,
    fullscreenRootRef,
  } = useViewController({
    userList,
    onClose,
    showConfirmationExit,
    hideChatOnStart,
    chatInLive,
  });

  const { onSwitchCam, onToggleCam, onToggleMic, onToggleScreenSharing, onToggleBlur } = useController(
    managedConstraints,
    onChangeConstraints
  );

  const [managedPlayer, setManagedPlayer] = useState<HTMLVideoElement | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const { pipActive, isPipSupported, togglePip } = usePictureInPicture();

  // For PiP: prefer the managed (remote) player, fall back to any playing video in the stream view
  const handleTogglePip = useCallback(() => {
    if (pipActive) {
      togglePip(null); // exit PiP
      return;
    }
    if (managedPlayer && managedPlayer.readyState >= 2) {
      togglePip(managedPlayer);
      return;
    }
    // Fallback: find any playing video in the stream layout
    const videos = document.querySelectorAll<HTMLVideoElement>(
      ".main-stream-view__video, .produce-stream-item__video, .second-stream-view__video"
    );
    for (const v of videos) {
      if (v.readyState >= 2 && !v.paused) {
        togglePip(v);
        return;
      }
    }
    // Last resort: try any video on page
    const anyVideo = document.querySelector<HTMLVideoElement>("video[autoplay]");
    if (anyVideo) togglePip(anyVideo);
  }, [togglePip, managedPlayer, pipActive]);

  useEffect(() => {
    onSelectedStreamChange?.(selectedStreamId);
  }, [selectedStreamId, onSelectedStreamChange]);

  const mobileChatMode = displayControls.rightPanel === RightPanelView.CHAT && !desktopUp;
  const mobileChatInLive = chatInLive && !desktopUp;
  const settingsMode = displayControls.rightPanel === RightPanelView.SETTINGS;
  const usersListMode = displayControls.rightPanel === RightPanelView.USERS;
  const rightPanelHidden = displayControls.rightPanelHidden;
  const rightControlIndent = rightPanelHidden && !!selectedStreamId && streams.length > 2;

  const cls = ["stream-view"];
  if (mobileChatMode) cls.push("__chat-mode");
  if (usersListMode) cls.push("__users-mode");
  if (mobileLandscape) cls.push("__landscape-mode");
  if (!mobileChatInLive) cls.push("__modal-chat");
  if (rightPanelHidden) cls.push("__right-panel-hidden");
  if (displayControls.fullscreen) cls.push("__fullscreen");
  if (displayControls.fullscreen && displayControls.controlsIdle) cls.push("__controls-idle");
  if (className) cls.push(className);

  // Double-click anywhere on the video area toggles fullscreen. We bind to
  // the wrapper instead of individual <video> elements so it works in grid
  // / focus / screen-share layouts uniformly.
  const onVideoDoubleClick = useCallback(() => {
    if (isMobile()) return;
    toggleFullscreen();
  }, [toggleFullscreen]);

  return (
    <div ref={fullscreenRootRef} className={cls.join(" ")} onClick={onShowControls}>
      <div className="stream-view__left-side">
        <div className="stream-view__header">
          <div className="stream-view__header-inner">
            {/* Timer pills are pinned: they stay visible after controls fade
                so users always know how long the call (and any active
                recording) has been running. */}
            {slotTimer && <div className="stream-view__header-timer">{slotTimer}</div>}
            <TransitionControl className="stream-view__header-buttons-wrap" showControl={showControls || usersListMode}>
              <div className="stream-view__header-buttons">
                {!disableUserList && (
                  <>
                    <div className="stream-view__header-bubble mr-2">
                      <span>{usersCount}</span>
                    </div>
                    <BaseButton
                      className={
                        "stream-view__header-btn" +
                        (produce ? "" : " __disabled") +
                        (usersListMode && !rightPanelHidden ? " __active" : "")
                      }
                      name={"users"}
                      disabled={!produce}
                      onClick={() =>
                        changeRightPanel(
                          usersListMode && !rightPanelHidden ? RightPanelView.HIDE_ALL : RightPanelView.USERS
                        )
                      }
                    >
                      <BaseIcon className="stream-view__header-icon">consultants</BaseIcon>
                    </BaseButton>
                  </>
                )}
                {slotHeaderButtons && slotHeaderButtons}
                {managedConstraints && desktopUp && !isMobile() && (
                  <BaseButton
                    className={"stream-view__header-btn" + (settingsMode && !rightPanelHidden ? " __active" : "")}
                    name={"settings"}
                    onClick={() =>
                      changeRightPanel(
                        settingsMode && !rightPanelHidden ? RightPanelView.HIDE_ALL : RightPanelView.SETTINGS
                      )
                    }
                  >
                    <BaseIcon className="stream-view__header-icon">settings</BaseIcon>
                  </BaseButton>
                )}
                {desktopUp && !isMobile() && (
                  <BaseButton
                    className={"stream-view__header-btn" + (displayControls.fullscreen ? " __active" : "")}
                    name={"fullscreen"}
                    onClick={toggleFullscreen}
                    title={displayControls.fullscreen ? t("Exit fullscreen (Esc / F)") : t("Fullscreen (F)")}
                  >
                    <BaseIcon className="stream-view__header-icon">
                      {displayControls.fullscreen ? "minimize" : "maximize"}
                    </BaseIcon>
                  </BaseButton>
                )}
              </div>
            </TransitionControl>
          </div>
        </div>

        <div className={"stream-view__video-wrapper"} onDoubleClick={onVideoDoubleClick}>
          <TransitionControl
            className="stream-view__video-wrapper-header"
            showControl={desktopUp || (!showControls && !usersListMode)}
          >
            {produceUser && (
              <div className="stream-view__live-user">
                <BaseAvatar className="stream-view__live-user-avatar" size={30} user={produceUser} />
                <p className="stream-view__live-user-name label fw-semibold">{produceUser.screenName}</p>
              </div>
            )}
          </TransitionControl>
          {slotVideoScreen ? (
            slotVideoScreen
          ) : (
            <StreamsLayout
              streams={streams}
              me={me}
              setManagedPlayer={setManagedPlayer}
              selectedStreamId={selectedStreamId}
              setSelectedStreamId={setSelectedStreamId}
              showControl={desktopUp || (!showControls && !usersListMode)}
              large={rightPanelHidden}
            />
          )}
          {showStreamPlayButton && <PlayStreamButton />}
          <div className={["stream-view__shadow", rightControlIndent ? "__right-indent" : ""].join(" ")} />
          <PanelControlButtons
            className={["stream-view__control-buttons", rightControlIndent ? "__right-indent" : ""].join(" ")}
            loading={loading}
            showControlsButtons={!forceHideControlButtons && showControlsButtons}
            constraints={managedConstraints}
            onClickEndCall={onClickEndCall}
            onSwitchCam={onSwitchCam}
            onToggleMic={onToggleMic}
            onToggleCam={onToggleCam}
            onToggleBlur={onToggleBlur}
            onToggleChat={onOpenChat}
            onToggleScreenSharing={onToggleScreenSharing}
            onToggleGrid={() => setSelectedStreamId(null)}
            showChatButton={rightPanelHidden || displayControls.rightPanel !== RightPanelView.CHAT}
            onTogglePip={handleTogglePip}
            pipActive={pipActive}
            isPipSupported={isPipSupported}
            player={hideVolumeControl ? null : managedPlayer}
            slotControlButtons={slotControlButtons}
            muted={me?.user?.muted}
            showGridBtn={!!selectedStreamId && streams.length > 2}
            hasUnreadMessage={displayControls.hasUnreadMessage}
          />
        </div>

        {displayControls.confirmationExit && (
          <ModalExitConfirmation
            slotContentConfirmationExit={slotContentConfirmationExit}
            onConfirm={onCloseEvent}
            onCancel={() => changeControls("confirmationExit", false)}
          />
        )}
      </div>
      <div
        className={
          "stream-view__right-side" +
          (mobileChatMode ? " __chat-mode" : "") +
          (usersListMode ? " __users-mode" : "") +
          (mobileLandscape ? " __landscape-mode" : "") +
          ((mobileChatMode && !mobileChatInLive) || usersListMode ? " __modal-mode" : "")
        }
      >
        <RightPanel
          loading={loading}
          disableAttachments={disableAttachments}
          token={token}
          threadId={threadId}
          devices={audioInputs && videoInputs && { audioInputs, videoInputs }}
          onChangeConstraints={onChangeConstraints}
          constraints={managedConstraints}
          userList={userList}
          modalChatView={!chatInLive}
          enableUserSearch={enableUserSearch}
          allowGuestMessage={allowGuestMessage}
          me={me?.user}
          userListItemIcon={userListItemIcon}
          showSection={displayControls.rightPanel}
          includeGuestsList={includeGuestsList}
          changeControls={changeRightPanel}
          setHasUnreadMessage={setHasUnreadMessage}
        />
      </div>
    </div>
  );
}

export default StreamView;
